import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { QueryKeys } from '@gokrew/models/constants';
import type {
  MessagingBootstrap,
  MessagingEvent,
  MessagingSettings,
} from '@gokrew/models/messaging';
import {
  createMessagingReplay,
  createMessagingReconnect,
  isolateMessagingDrafts,
  latestMessagingSettings,
} from './state';
import type { MessagingContextValue, MessagingProviderProps } from './types';

import { MessagingContext } from './context';

export const MessagingProvider = ({
  api,
  companyId,
  userId,
  portal,
  socketUrl,
  getToken,
  refreshToken,
  children,
}: MessagingProviderProps) => {
  const queryClient = useQueryClient();
  const scope = `${portal}:${companyId ?? ''}:${userId ?? ''}`;
  const enabled = !!companyId && !!userId;
  useEffect(() => {
    if (userId) isolateMessagingDrafts(portal, userId);
  }, [portal, userId]);
  const bootstrapKey = useMemo(
    () => [QueryKeys.MessagingSettings, scope, 'bootstrap'],
    [scope]
  );
  const versionedSettings = useMemo(
    () => ({
      latest:
        queryClient.getQueryData<MessagingBootstrap>(bootstrapKey)?.settings,
    }),
    [bootstrapKey, queryClient]
  );
  const realtimeRetry = useRef<() => void>(() => undefined);
  const bootstrapQuery = useQuery({
    queryKey: bootstrapKey,
    queryFn: async () => {
      const result = await api.bootstrap();
      if (result.companyId !== companyId || result.userId !== userId)
        throw new Error('Messaging session changed');
      versionedSettings.latest = latestMessagingSettings(
        versionedSettings.latest,
        result.settings
      );
      return { ...result, settings: versionedSettings.latest };
    },
    enabled,
    refetchInterval: 30_000,
    retry: 1,
    throwOnError: false,
  });
  const unreadQuery = useQuery({
    queryKey: [QueryKeys.MessagingUnread, scope],
    queryFn: () => api.unread(),
    enabled,
    refetchInterval: 15_000,
    retry: 1,
    throwOnError: false,
  });
  const [connection, setConnection] = useState<
    MessagingContextValue['connection']
  >(navigator.onLine ? 'connecting' : 'offline');

  const applySettings = useCallback(
    (settings: MessagingSettings) => {
      if (settings.companyId !== companyId) return;
      versionedSettings.latest = latestMessagingSettings(
        versionedSettings.latest,
        settings
      );
      const latest = versionedSettings.latest;
      queryClient.setQueryData<MessagingBootstrap>(bootstrapKey, (previous) =>
        previous ? { ...previous, settings: latest } : previous
      );
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MessagingConversations, scope],
      });
    },
    [bootstrapKey, companyId, queryClient, scope, versionedSettings]
  );

  const applyEvent = useCallback(
    (event: MessagingEvent) => {
      if (event.companyId !== companyId) return;
      if (event.type === 'settings') applySettings(event.settings);
      if ('conversationId' in event)
        void queryClient.invalidateQueries({
          queryKey: [QueryKeys.MessagingMessages, scope, event.conversationId],
        });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MessagingConversations, scope],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MessagingUnread, scope],
      });
    },
    [applySettings, companyId, queryClient, scope]
  );

  const replay = useMemo(() => {
    let initialCursor: string | null = null;
    try {
      initialCursor = sessionStorage.getItem(`messaging-cursor:${scope}`);
    } catch {
      /* Storage may be unavailable. */
    }
    return createMessagingReplay({
      initialCursor,
      fetch: (after) => api.sync(after),
      apply: applyEvent,
      persist: (next) => {
        try {
          sessionStorage.setItem(`messaging-cursor:${scope}`, next);
        } catch {
          /* In-memory sync remains available. */
        }
      },
    });
  }, [api, applyEvent, scope]);
  useEffect(() => {
    replay.activate();
    return () => replay.dispose();
  }, [replay]);
  const synchronize = useCallback(() => replay.run(), [replay]);
  useEffect(() => {
    if (
      !bootstrapQuery.data ||
      !replay.active ||
      bootstrapQuery.data?.companyId !== companyId ||
      bootstrapQuery.data?.userId !== userId
    )
      return;
    replay.initialize(bootstrapQuery.data.syncCursor);
    void synchronize().catch(() => undefined);
  }, [bootstrapQuery.data, companyId, userId, replay, synchronize]);

  useEffect(() => {
    if (!enabled || !companyId) return;
    let disposed = false;
    let currentToken: string | null = null;
    const socket = io(socketUrl, {
      path: '/api/socket.io',
      transports: ['websocket'],
      autoConnect: false,
      reconnectionDelayMax: 15_000,
      auth: (done) => {
        void getToken()
          .then((token) => {
            currentToken = token;
            done({ token, companyId });
          })
          .catch(() => done({ token: null, companyId }));
      },
    });
    let serverDisconnected = false;
    const recovery = createMessagingReconnect({
      isOnline: () => navigator.onLine && !disposed,
      validate: async () => {
        const token = await getToken();
        if (!token || disposed) return false;
        const current = await api.bootstrap();
        return (
          !disposed &&
          current.companyId === companyId &&
          current.userId === userId
        );
      },
      reconnect: () => {
        if (!disposed && serverDisconnected) socket.connect();
      },
    });
    const refreshAll = () => {
      if (disposed || !replay.active) return;
      void synchronize().catch(() => undefined);
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MessagingConversations, scope],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MessagingUnread, scope],
      });
    };
    socket.on('connect', () => {
      serverDisconnected = false;
      recovery.cancel();
      if (!disposed) setConnection('connected');
      refreshAll();
    });
    socket.on('messaging.ready', refreshAll);
    socket.on('messaging.event', (event: MessagingEvent) => {
      if (disposed || !replay.active) return;
      applyEvent(event);
      void synchronize().catch(() => undefined);
    });
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        serverDisconnected = true;
        recovery.schedule();
      }
      if (!disposed) setConnection(navigator.onLine ? 'connecting' : 'offline');
    });
    socket.on('connect_error', (error: Error) => {
      if (disposed) return;
      setConnection(navigator.onLine ? 'connecting' : 'offline');
      if (!socket.active) {
        serverDisconnected = true;
        recovery.schedule();
      }
      if (/token|unauthor|auth/i.test(error.message))
        void refreshToken()
          .then((token) => {
            if (token && !disposed) socket.connect();
          })
          .catch(() => undefined);
    });
    const online = () => {
      setConnection('connecting');
      recovery.cancel();
      if (serverDisconnected) recovery.schedule();
      else socket.connect();
      refreshAll();
    };
    const offline = () => {
      recovery.cancel();
      setConnection('offline');
      socket.disconnect();
    };
    const focus = () => {
      if (document.visibilityState === 'visible') {
        refreshAll();
        if (serverDisconnected) {
          recovery.cancel();
          recovery.schedule();
        }
      }
    };
    realtimeRetry.current = online;
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', focus);
    const backfill = window.setInterval(refreshAll, 15_000);
    const renew = window.setInterval(() => {
      void getToken()
        .then((token) => {
          if (token && token !== currentToken && !disposed) {
            socket.disconnect();
            socket.connect();
          }
        })
        .catch(() => undefined);
    }, 45_000);
    if (navigator.onLine) socket.connect();
    return () => {
      disposed = true;
      recovery.dispose();
      if (realtimeRetry.current === online)
        realtimeRetry.current = () => undefined;
      window.clearInterval(backfill);
      window.clearInterval(renew);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', focus);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [
    api,
    applyEvent,
    companyId,
    enabled,
    getToken,
    queryClient,
    refreshToken,
    scope,
    socketUrl,
    replay,
    synchronize,
    userId,
  ]);

  const refresh = useCallback(async () => {
    realtimeRetry.current();
    await Promise.all([
      bootstrapQuery.refetch(),
      unreadQuery.refetch(),
      synchronize().catch(() => undefined),
    ]);
  }, [bootstrapQuery, unreadQuery, synchronize]);
  const value = useMemo<MessagingContextValue>(
    () => ({
      api,
      scope,
      bootstrap: bootstrapQuery.data,
      settings: bootstrapQuery.data?.settings,
      unreadCount:
        unreadQuery.data?.unreadCount ?? bootstrapQuery.data?.unreadCount ?? 0,
      connection,
      isLoading: bootstrapQuery.isLoading,
      isError: bootstrapQuery.isError && !bootstrapQuery.data,
      refresh,
      applyEvent,
      applySettings,
    }),
    [
      api,
      applyEvent,
      applySettings,
      bootstrapQuery.data,
      bootstrapQuery.isError,
      bootstrapQuery.isLoading,
      connection,
      refresh,
      scope,
      unreadQuery.data,
    ]
  );
  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
